import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import JSZip from 'jszip';
import {
  Linkedin,
  RefreshCw,
  Copy,
  CheckCircle,
  AlertTriangle,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Briefcase,
  FileText,
  MessageSquare,
  Star,
  TrendingUp,
  Eye,
  Zap,
  Edit2,
  Save,
  Upload,
  Package,
  Users,
  GraduationCap,
} from 'lucide-react';
import { API_BASE } from '../config';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { scoreColor as themeScoreColor, scoreBgClass } from '../utils/themeColors';

// ─── Types ─────────────────────────────────────────────────

interface ChecklistItem {
  label: string;
  done: boolean;
  note?: string;
}

interface DataSources {
  resume_count: number;
  interview_count: number;
  cover_letter_count: number;
  linkedin_imported: boolean;
}

interface OptimizerData {
  overall_score: number;
  headline_score: number;
  about_score: number;
  skills_score: number;
  experience_score: number;
  completeness_score: number;
  current_headline: string;
  current_about: string;
  linkedin_url: string;
  optimized_headlines: string[];
  optimized_about: string;
  skills_to_add: string[];
  skills_to_reorder: string[];
  skills_to_remove: string[];
  recruiter_keywords: string[];
  experience_tip: string;
  completeness_checklist: ChecklistItem[];
  data_sources: DataSources;
  generated_at: string;
}

// ─── LinkedIn Export Parsing ────────────────────────────────

interface ParsedExport {
  profile: { headline: string; summary: string; location: string; first_name: string; last_name: string };
  skills: string[];
  positions: Array<{ company: string; title: string; description: string; started_on: string; finished_on: string }>;
  education: Array<{ school: string; degree: string; field: string; start_date: string; end_date: string }>;
  certifications: Array<{ name: string; authority?: string; started_on?: string; finished_on?: string }>;
}

/** Minimal CSV parser that handles quoted fields with embedded commas / newlines */
function parseCsv(text: string): Array<Record<string, string>> {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQ = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      if (inQ && text[i + 1] === '"') { cell += '"'; i++; }
      else { inQ = !inQ; }
    } else if (ch === ',' && !inQ) {
      row.push(cell.trim()); cell = '';
    } else if ((ch === '\n' || ch === '\r') && !inQ) {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell.trim()); cell = '';
      if (row.some(Boolean)) rows.push(row);
      row = [];
    } else {
      cell += ch;
    }
  }
  if (cell || row.length) { row.push(cell.trim()); if (row.some(Boolean)) rows.push(row); }

  if (rows.length < 2) return [];
  const headers = rows[0].map((h) => h.replace(/^\uFEFF/, '')); // strip BOM
  return rows.slice(1).map((vals) => {
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => { obj[h] = vals[i] ?? ''; });
    return obj;
  });
}

/** Finds a file inside the ZIP regardless of subfolder */
async function readZipFile(zip: JSZip, pattern: RegExp): Promise<string> {
  const file = zip.file(pattern)[0];
  return file ? file.async('string') : '';
}

async function parseLinkedInExport(zipFile: File): Promise<ParsedExport> {
  const zip = await JSZip.loadAsync(zipFile);

  const [profileCsv, skillsCsv, positionsCsv, educationCsv, certificationsCsv] = await Promise.all([
    readZipFile(zip, /Profile\.csv$/i),
    readZipFile(zip, /Skills\.csv$/i),
    readZipFile(zip, /Positions\.csv$/i),
    readZipFile(zip, /Education\.csv$/i),
    readZipFile(zip, /Certifications\.csv$/i),
  ]);

  // Profile (single row)
  const profileRows = parseCsv(profileCsv);
  const p = profileRows[0] ?? {};
  const profile = {
    first_name: p['First Name'] ?? '',
    last_name:  p['Last Name']  ?? '',
    headline:   p['Headline']   ?? '',
    summary:    p['Summary']    ?? '',
    location:   p['Geo Location'] ?? p['Address'] ?? '',
  };

  // Skills (one per row)
  const skills = parseCsv(skillsCsv)
    .map((r) => r['Name'] ?? Object.values(r)[0] ?? '')
    .filter(Boolean);

  // Positions
  const positions = parseCsv(positionsCsv).map((r) => ({
    company:     r['Company Name'] ?? '',
    title:       r['Title']        ?? '',
    description: r['Description']  ?? '',
    location:    r['Location']     ?? '',
    started_on:  r['Started On']   ?? '',
    finished_on: r['Finished On']  ?? '',
  }));

  // Education
  const education = parseCsv(educationCsv).map((r) => ({
    school:     r['School Name']  ?? '',
    degree:     r['Degree Name']  ?? '',
    field:      r['Notes']        ?? '',
    start_date: r['Start Date']   ?? '',
    end_date:   r['End Date']     ?? '',
  }));

  // Certifications (optional — not all exports include this file)
  const certifications = parseCsv(certificationsCsv)
    .map((r) => ({
      name:        (r['Name'] ?? r['Certificate Name'] ?? '').trim(),
      authority:   (r['Authority'] ?? r['Issuing Organization'] ?? '').trim(),
      started_on:  r['Started On'] ?? '',
      finished_on: r['Finished On'] ?? '',
    }))
    .filter((c) => c.name.length > 0);

  return { profile, skills, positions, education, certifications };
}

// ─── Helpers ────────────────────────────────────────────────

function formatAge(isoString: string): string {
  const ms = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

function scoreColor(score: number, isDark: boolean = false): string {
  return themeScoreColor(score, isDark);
}

function scoreBg(score: number): string {
  return scoreBgClass(score);
}

function scoreLabel(score: number): string {
  if (score >= 80) return 'Excellent';
  if (score >= 60) return 'Good';
  if (score >= 40) return 'Needs Work';
  return 'Critical';
}

// ─── Sub-Components ─────────────────────────────────────────

function CircularGauge({ score }: { score: number }) {
  const { isDark } = useTheme();
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = scoreColor(score, isDark);

  return (
    <div className="flex flex-col items-center">
      <svg width="148" height="148" viewBox="0 0 148 148">
        {/* Track */}
        <circle cx="74" cy="74" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="12" className="dark:stroke-slate-700" />
        {/* Progress */}
        <circle
          cx="74" cy="74" r={radius}
          fill="none"
          stroke={color}
          strokeWidth="12"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 74 74)"
          style={{ transition: 'stroke-dashoffset 1.2s ease-in-out' }}
        />
        {/* Score text */}
        <text x="74" y="68" textAnchor="middle" fontSize="30" fontWeight="700" fill={color}>
          {score}
        </text>
        <text x="74" y="85" textAnchor="middle" fontSize="11" fill="#6b7280">
          / 100
        </text>
        <text x="74" y="100" textAnchor="middle" fontSize="10" fill={color} fontWeight="600">
          {scoreLabel(score)}
        </text>
      </svg>
      <p className="text-sm font-semibold text-gray-700 dark:text-slate-300 mt-1">Recruiter Magnet Score</p>
      <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Your LinkedIn visibility index</p>
    </div>
  );
}

function ScoreBar({ label, score, icon: Icon }: { label: string; score: number; icon: React.ElementType }) {
  const { isDark } = useTheme();
  const color = scoreColor(score, isDark);
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 text-gray-400 dark:text-slate-500 flex-shrink-0" />
      <div className="flex-1">
        <div className="flex justify-between mb-1">
          <span className="text-xs text-gray-600 dark:text-slate-400">{label}</span>
          <span className="text-xs font-semibold" style={{ color }}>{score}</span>
        </div>
        <div className="h-1.5 bg-gray-100 dark:bg-slate-800/50 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ backgroundColor: color }}
            initial={{ width: 0 }}
            animate={{ width: `${score}%` }}
            transition={{ duration: 0.8, delay: 0.2 }}
          />
        </div>
      </div>
    </div>
  );
}

function CopyButton({ text, id, copiedId, onCopy }: {
  text: string; id: string; copiedId: string | null;
  onCopy: (text: string, id: string) => void;
}) {
  const copied = copiedId === id;
  return (
    <motion.button
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
      onClick={() => onCopy(text, id)}
      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
        copied
          ? 'bg-green-100 text-green-700 border border-green-200'
          : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700 border border-gray-200 dark:border-slate-700'
      }`}
    >
      {copied
        ? <><CheckCircle className="w-3.5 h-3.5" />Copied!</>
        : <><Copy className="w-3.5 h-3.5" />Copy</>}
    </motion.button>
  );
}

function SectionCard({ title, score, icon: Icon, children, defaultExpanded = true }: {
  title: string;
  score?: number;
  icon: React.ElementType;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-slate-800 overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center">
            <Icon className="w-4 h-4 text-indigo-600" />
          </div>
          <span className="font-semibold text-gray-900 dark:text-white">{title}</span>
        </div>
        <div className="flex items-center gap-3">
          {score !== undefined && (
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${scoreBg(score)}`}>
              {score}/100
            </span>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400 dark:text-slate-500" /> : <ChevronDown className="w-4 h-4 text-gray-400 dark:text-slate-500" />}
        </div>
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 border-t border-gray-100 dark:border-slate-800">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-10 bg-gray-100 dark:bg-slate-800/50 rounded-xl w-2/3" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-gray-100 dark:bg-slate-800/50 rounded-xl h-48" />
        <div className="bg-gray-100 dark:bg-slate-800/50 rounded-xl h-48" />
        <div className="bg-gray-100 dark:bg-slate-800/50 rounded-xl h-48" />
      </div>
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="bg-gray-100 dark:bg-slate-800/50 rounded-xl h-32" />
      ))}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────

const LinkedInOptimizer: React.FC = () => {
  const { isDark } = useTheme();
  const [data, setData] = useState<OptimizerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [aboutExpanded, setAboutExpanded] = useState(false);

  // Profile input card state
  const [profileCardExpanded, setProfileCardExpanded] = useState(false);
  const [editHeadline, setEditHeadline] = useState('');
  const [editAbout, setEditAbout] = useState('');
  const [editLinkedInUrl, setEditLinkedInUrl] = useState('');
  const [saving, setSaving] = useState(false);

  // LinkedIn export importer state
  const [importerExpanded, setImporterExpanded] = useState(false);
  const [parsedExport, setParsedExport] = useState<ParsedExport | null>(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importSuccess, setImportSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/intelligence/linkedin-optimizer`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json() as OptimizerData;
      setData(json);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load analysis');
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchData().finally(() => setLoading(false));
  }, [fetchData]);

  // Sync edit fields from loaded data (pre-fill with existing content)
  useEffect(() => {
    if (data) {
      setEditHeadline(data.current_headline || '');
      setEditAbout(data.current_about || '');
      setEditLinkedInUrl(data.linkedin_url || '');
      // Auto-expand the card if LinkedIn is not imported (prompts user to fill in)
      if (!data.data_sources.linkedin_imported) {
        setProfileCardExpanded(true);
      }
    }
  }, [data?.generated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/intelligence/linkedin-optimizer/update-profile-text`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          headline: editHeadline || undefined,
          about: editAbout || undefined,
          linkedin_url: editLinkedInUrl || undefined,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json() as OptimizerData;
      setData(json);
      setError(null);
      setProfileCardExpanded(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
    setSaving(false);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    setParsedExport(null);
    setImportSuccess(false);
    try {
      const parsed = await parseLinkedInExport(file);
      if (!parsed.skills.length && !parsed.profile.headline && !parsed.positions.length) {
        setError('Could not find LinkedIn data in this ZIP. Make sure you downloaded the correct export file from LinkedIn.');
      } else {
        setParsedExport(parsed);
        setError(null);
      }
    } catch {
      setError('Failed to read ZIP. Make sure it is the unmodified LinkedIn data export file.');
    }
    setParsing(false);
    // Reset file input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImportLinkedIn = async () => {
    if (!parsedExport) return;
    setImporting(true);
    try {
      const res = await fetch(`${API_BASE}/api/intelligence/linkedin-optimizer/import-linkedin-export`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsedExport),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json() as { imported: { skills_count: number }; optimizer: OptimizerData };
      setData(json.optimizer);
      setError(null);
      setImportSuccess(true);
      setImporterExpanded(false);
      setParsedExport(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed');
    }
    setImporting(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API_BASE}/api/intelligence/linkedin-optimizer/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json() as OptimizerData;
      setData(json);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Refresh failed');
    }
    setRefreshing(false);
  };

  const handleCopy = (text: string, id: string) => {
    void navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Linkedin className="w-6 h-6 text-primary-600" />
              LinkedIn Optimizer
            </h1>
            <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">Generating your personalized analysis...</p>
          </div>
        </div>
        <LoadingSkeleton />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-3" />
          <p className="text-red-800 dark:text-red-300 font-medium">Failed to load LinkedIn optimizer</p>
          <p className="text-red-600 dark:text-red-400 text-sm mt-1">{error}</p>
          <button
            onClick={() => { setLoading(true); void fetchData().finally(() => setLoading(false)); }}
            className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const d = data!;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* ── Page Header ── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Linkedin className="w-6 h-6 text-primary-600" />
            LinkedIn Optimizer
          </h1>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
            AI-powered analysis using all your uploaded data to maximize recruiter visibility
            {data && (
              <span className="ml-2 text-gray-400 dark:text-slate-500">
                · Last analyzed {formatAge(d.generated_at)}
              </span>
            )}
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => void handleRefresh()}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 transition-colors shadow-sm"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh Analysis'}
        </motion.button>
      </div>

      {/* ── Current LinkedIn Profile Card (always shown, auto-expanded when not imported) ── */}
      <div className="bg-white dark:bg-navy-800 rounded-xl border border-primary-200 dark:border-primary-800/30 overflow-hidden shadow-sm">
        <button
          className="w-full flex items-center justify-between px-6 py-4 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
          onClick={() => setProfileCardExpanded(!profileCardExpanded)}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <Edit2 className="w-4 h-4 text-primary-600" />
            </div>
            <div className="text-left">
              <span className="font-semibold text-gray-900 dark:text-white text-sm">Your Current LinkedIn Content</span>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                {d.data_sources.linkedin_imported
                  ? 'Profile imported — paste updated content to re-score'
                  : 'Paste your current headline & About section for accurate scoring'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!d.data_sources.linkedin_imported && (
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                Needed for accurate score
              </span>
            )}
            {profileCardExpanded
              ? <ChevronUp className="w-4 h-4 text-gray-400 dark:text-slate-500" />
              : <ChevronDown className="w-4 h-4 text-gray-400 dark:text-slate-500" />}
          </div>
        </button>

        <AnimatePresence>
          {profileCardExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <div className="px-6 pb-6 pt-5 space-y-4 border-t border-primary-100 dark:border-primary-800/30">
                {!d.data_sources.linkedin_imported && (
                  <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded-lg p-3">
                    <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">LinkedIn not imported</p>
                      <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                        Paste your current content below for an accurate score.
                        Or{' '}
                        <Link to="/intelligence" className="underline hover:text-amber-900 dark:hover:text-amber-200">
                          import your full profile on the Intelligence page
                        </Link>.
                      </p>
                    </div>
                  </div>
                )}

                {/* LinkedIn URL */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                    LinkedIn Profile URL
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="url"
                      value={editLinkedInUrl}
                      onChange={(e) => setEditLinkedInUrl(e.target.value)}
                      placeholder="https://linkedin.com/in/your-name"
                      className="flex-1 text-sm border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300 dark:bg-slate-800 dark:text-white"
                    />
                    {editLinkedInUrl && (
                      <a
                        href={editLinkedInUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-primary-600 hover:text-primary-800 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-100 dark:border-primary-800/30"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>

                {/* Headline */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                    Current LinkedIn Headline
                    <span className="ml-2 normal-case font-normal text-gray-400 dark:text-slate-500">
                      ({editHeadline.length}/220 chars)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={editHeadline}
                    onChange={(e) => setEditHeadline(e.target.value)}
                    maxLength={220}
                    placeholder="e.g. VP Engineering | Cloud Infrastructure | AI Platforms | Scale-up Leader"
                    className="w-full text-sm border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                {/* About section */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                    Current LinkedIn About / Summary
                    <span className="ml-2 normal-case font-normal text-gray-400 dark:text-slate-500">
                      ({editAbout.length} chars · target 1,500–2,000)
                    </span>
                  </label>
                  <textarea
                    value={editAbout}
                    onChange={(e) => setEditAbout(e.target.value)}
                    placeholder="Paste your current LinkedIn About section here. The AI will score it against market keywords and generate an optimized version."
                    rows={8}
                    className="w-full text-sm border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none dark:bg-slate-800 dark:text-white"
                  />
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-400 dark:text-slate-500">
                      Include keywords from your target market, a value proposition, and a call-to-action at the end.
                    </p>
                    <div className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      editAbout.length >= 1500 ? 'bg-green-100 text-green-700'
                      : editAbout.length >= 800 ? 'bg-primary-100 text-primary-700'
                      : editAbout.length >= 300 ? 'bg-amber-100 text-amber-700'
                      : 'bg-red-100 text-red-700'
                    }`}>
                      {editAbout.length >= 1500 ? 'Excellent length'
                        : editAbout.length >= 800 ? 'Good length'
                        : editAbout.length >= 300 ? 'Too short'
                        : 'Very short'}
                    </div>
                  </div>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => void handleSaveProfile()}
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60 transition-colors shadow-sm"
                >
                  {saving
                    ? <><RefreshCw className="w-4 h-4 animate-spin" />Saving & re-analyzing...</>
                    : <><Save className="w-4 h-4" />Save & Refresh Analysis</>}
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── LinkedIn Data Export Importer ── */}
      <div className="bg-white dark:bg-navy-800 rounded-xl border border-green-200 dark:border-green-800/30 overflow-hidden shadow-sm">
        <button
          className="w-full flex items-center justify-between px-6 py-4 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
          onClick={() => setImporterExpanded(!importerExpanded)}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Package className="w-4 h-4 text-green-600" />
            </div>
            <div className="text-left">
              <span className="font-semibold text-gray-900 dark:text-white text-sm">Import LinkedIn Data Export</span>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                {importSuccess
                  ? '✓ Import complete — all skills, positions & education imported'
                  : 'Upload your LinkedIn ZIP export to pull all skills, experience & education automatically'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!d.data_sources.linkedin_imported && !importSuccess && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                Recommended
              </span>
            )}
            {importerExpanded
              ? <ChevronUp className="w-4 h-4 text-gray-400 dark:text-slate-500" />
              : <ChevronDown className="w-4 h-4 text-gray-400 dark:text-slate-500" />}
          </div>
        </button>

        <AnimatePresence>
          {importerExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <div className="px-6 pb-6 pt-5 space-y-5 border-t border-green-100 dark:border-green-800/30">
                {/* Step-by-step instructions */}
                <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl p-4 space-y-3">
                  <p className="text-xs font-bold text-gray-700 dark:text-slate-300 uppercase tracking-wider">
                    How to get your LinkedIn data export:
                  </p>
                  <ol className="space-y-2 text-sm text-gray-600 dark:text-slate-400">
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-green-500 text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-bold">1</span>
                      <span>
                        Go to{' '}
                        <a
                          href="https://www.linkedin.com/mypreferences/d/download-my-data"
                          target="_blank" rel="noopener noreferrer"
                          className="text-primary-600 underline hover:text-primary-800 font-medium"
                        >
                          LinkedIn → Settings → Data Privacy → Get a copy of your data
                        </a>
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-green-500 text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-bold">2</span>
                      <span>
                        Select <strong>{"\"Want something in particular?\""}</strong> and tick:
                        <span className="ml-1 font-mono text-xs bg-gray-200 dark:bg-slate-700 px-1.5 py-0.5 rounded">Profile</span>{' '}
                        <span className="font-mono text-xs bg-gray-200 dark:bg-slate-700 px-1.5 py-0.5 rounded">Skills</span>{' '}
                        <span className="font-mono text-xs bg-gray-200 dark:bg-slate-700 px-1.5 py-0.5 rounded">Positions</span>{' '}
                        <span className="font-mono text-xs bg-gray-200 dark:bg-slate-700 px-1.5 py-0.5 rounded">Education</span>
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-green-500 text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-bold">3</span>
                      <span>Click <strong>Request archive</strong> — LinkedIn emails a download link (usually within minutes for selective export).</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-green-500 text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-bold">4</span>
                      <span>Download the ZIP and upload it below.</span>
                    </li>
                  </ol>
                </div>

                {/* File drop zone */}
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".zip"
                    onChange={(e) => void handleFileSelect(e)}
                    className="hidden"
                    id="linkedin-zip-input"
                  />
                  <label
                    htmlFor="linkedin-zip-input"
                    className={`flex flex-col items-center justify-center gap-3 border-2 border-dashed rounded-xl p-8 cursor-pointer transition-colors ${
                      parsing
                        ? 'border-green-300 bg-green-50 dark:bg-green-900/20'
                        : 'border-gray-300 dark:border-slate-700 hover:border-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                    }`}
                  >
                    {parsing ? (
                      <>
                        <RefreshCw className="w-8 h-8 text-green-500 animate-spin" />
                        <p className="text-sm font-medium text-green-700">Reading LinkedIn export...</p>
                      </>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-gray-400 dark:text-slate-500" />
                        <div className="text-center">
                          <p className="text-sm font-medium text-gray-700 dark:text-slate-300">Drop your LinkedIn ZIP here</p>
                          <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">or click to browse · .zip files only</p>
                        </div>
                      </>
                    )}
                  </label>
                </div>

                {/* Preview after parsing */}
                {parsedExport && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-4"
                  >
                    <p className="text-sm font-semibold text-gray-800 dark:text-white">Ready to import:</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 rounded-xl p-3 text-center">
                        <Linkedin className="w-5 h-5 text-green-600 mx-auto mb-1" />
                        <p className="text-lg font-bold text-green-800 dark:text-green-300">
                          {parsedExport.profile.headline ? '✓' : '✗'}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-slate-400">Headline</p>
                      </div>
                      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 rounded-xl p-3 text-center">
                        <Star className="w-5 h-5 text-green-600 mx-auto mb-1" />
                        <p className="text-lg font-bold text-green-800 dark:text-green-300">{parsedExport.skills.length}</p>
                        <p className="text-xs text-gray-600 dark:text-slate-400">Skills</p>
                      </div>
                      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 rounded-xl p-3 text-center">
                        <Users className="w-5 h-5 text-green-600 mx-auto mb-1" />
                        <p className="text-lg font-bold text-green-800 dark:text-green-300">{parsedExport.positions.length}</p>
                        <p className="text-xs text-gray-600 dark:text-slate-400">Positions</p>
                      </div>
                      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 rounded-xl p-3 text-center">
                        <GraduationCap className="w-5 h-5 text-green-600 mx-auto mb-1" />
                        <p className="text-lg font-bold text-green-800 dark:text-green-300">{parsedExport.education.length}</p>
                        <p className="text-xs text-gray-600 dark:text-slate-400">Education</p>
                      </div>
                    </div>

                    {parsedExport.profile.headline && (
                      <div className="bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-800 rounded-xl px-4 py-3">
                        <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 mb-1">Headline found:</p>
                        <p className="text-sm text-gray-800 dark:text-white">{parsedExport.profile.headline}</p>
                      </div>
                    )}

                    {parsedExport.skills.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 mb-2">Skills to import:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {parsedExport.skills.slice(0, 30).map((s, i) => (
                            <span key={i} className="text-xs bg-primary-50 text-primary-700 border border-primary-100 px-2 py-0.5 rounded-full">
                              {s}
                            </span>
                          ))}
                          {parsedExport.skills.length > 30 && (
                            <span className="text-xs text-gray-400 dark:text-slate-500 self-center">
                              +{parsedExport.skills.length - 30} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => void handleImportLinkedIn()}
                      disabled={importing}
                      className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-60 transition-colors shadow-sm"
                    >
                      {importing
                        ? <><RefreshCw className="w-4 h-4 animate-spin" />Importing & re-analyzing...</>
                        : <><CheckCircle className="w-4 h-4" />Import {parsedExport.skills.length} skills + profile data</>}
                    </motion.button>
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Top Row: Score + Sub-scores + Data Sources ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Circular Gauge */}
        <div className="bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-slate-800 p-6 flex items-center justify-center">
          <CircularGauge score={d.overall_score} />
        </div>

        {/* Sub-score Bars */}
        <div className="bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-slate-800 p-6 space-y-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-4">Score Breakdown</h3>
          <ScoreBar label="Headline" score={d.headline_score} icon={Briefcase} />
          <ScoreBar label="About / Summary" score={d.about_score} icon={FileText} />
          <ScoreBar label="Skills Coverage" score={d.skills_score} icon={Star} />
          <ScoreBar label="Experience Keywords" score={d.experience_score} icon={TrendingUp} />
          <ScoreBar label="Profile Completeness" score={d.completeness_score} icon={Eye} />
        </div>

        {/* Data Sources Panel */}
        <div className="bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-slate-800 p-6">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-4">Data Sources Used</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                <span className="text-sm text-gray-600 dark:text-slate-400">Resumes / CVs</span>
              </div>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                d.data_sources.resume_count > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400'
              }`}>
                {d.data_sources.resume_count} uploaded
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                <span className="text-sm text-gray-600 dark:text-slate-400">Interview Sessions</span>
              </div>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                d.data_sources.interview_count > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400'
              }`}>
                {d.data_sources.interview_count} completed
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                <span className="text-sm text-gray-600 dark:text-slate-400">Cover Letters</span>
              </div>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                d.data_sources.cover_letter_count > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400'
              }`}>
                {d.data_sources.cover_letter_count} uploaded
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Linkedin className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                <span className="text-sm text-gray-600 dark:text-slate-400">LinkedIn Profile</span>
              </div>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                d.data_sources.linkedin_imported ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
              }`}>
                {d.data_sources.linkedin_imported ? '✓ Imported' : '✗ Not imported'}
              </span>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-800">
              <p className="text-xs text-gray-400 dark:text-slate-500">
                Analysis also cross-references {' '}
                <span className="font-medium text-gray-600 dark:text-slate-400">live market data</span>
                {' '}from active job postings to surface recruiter keywords and demand signals.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Priority Action Plan ── */}
      {(() => {
        const actions: Array<{ action: string; detail: string; score: number }> = [];
        if (d.about_score < 50) {
          const chars = d.current_about?.length ?? 0;
          actions.push({
            action: 'Expand your LinkedIn About section to 1,500+ characters',
            detail: chars > 0
              ? `Currently ${chars.toLocaleString()} chars — copy the AI-generated About below and paste it into LinkedIn`
              : 'Copy the AI-generated About section below and paste it into LinkedIn',
            score: d.about_score,
          });
        }
        if (!d.data_sources.linkedin_imported) {
          actions.push({
            action: 'Import your LinkedIn data export (ZIP) above',
            detail: 'Unlocks accurate skills scoring — takes 2 minutes via LinkedIn Settings',
            score: 0,
          });
        }
        if (d.headline_score < 60) {
          actions.push({
            action: 'Replace your headline with one of the AI-optimised options below',
            detail: 'One click to copy, then paste directly into LinkedIn — instant keyword boost',
            score: d.headline_score,
          });
        }
        if (d.experience_score < 35) {
          actions.push({
            action: 'Add quantified metrics (%, $, team size) to your experience bullets',
            detail: 'See the Experience Bullet Optimization section below for a step-by-step formula',
            score: d.experience_score,
          });
        }
        const topActions = actions.slice(0, 3);
        if (topActions.length === 0) return null;
        return (
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border border-indigo-200 dark:border-indigo-800/30 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-indigo-600" />
              <h3 className="text-sm font-bold text-indigo-900 dark:text-indigo-300">Priority Action Plan</h3>
              <span className="ml-auto text-xs text-indigo-400 dark:text-indigo-500 font-medium">{topActions.length} high-impact action{topActions.length !== 1 ? 's' : ''} to improve your score</span>
            </div>
            <div className="space-y-2">
              {topActions.map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-white/70 dark:bg-navy-800/70 rounded-xl px-4 py-2.5">
                  <span className="w-5 h-5 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 dark:text-white font-medium">{item.action}</p>
                    <p className="text-xs text-indigo-500 dark:text-indigo-400 mt-0.5">{item.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── Headline Optimizer ── */}
      <SectionCard title="Headline Optimization" score={d.headline_score} icon={Zap}>
        <div className="pt-4 space-y-4">
          {/* Current headline */}
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-2">Current Headline</p>
            <div className="bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-800 rounded-xl px-4 py-3">
              <p className="text-sm text-gray-700 dark:text-slate-300">{d.current_headline || <span className="italic text-gray-400 dark:text-slate-500">No headline set</span>}</p>
            </div>
            {d.headline_score < 60 && (
              <div className="mt-2 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  {d.headline_score < 20 && 'Missing headline — this is the first thing recruiters see. Add one immediately.'}
                  {d.headline_score >= 20 && d.headline_score < 40 && 'Low keyword density. Recruiters search for specific skills and titles — add them here.'}
                  {d.headline_score >= 40 && d.headline_score < 60 && 'Good start but lacks value proposition. Tell recruiters what makes you different.'}
                </p>
              </div>
            )}
          </div>

          {/* Optimized alternatives */}
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-3">
              AI-Optimized Alternatives
              <span className="ml-2 normal-case text-gray-400 dark:text-slate-500 font-normal">(click Copy, then paste directly into LinkedIn)</span>
            </p>
            <div className="space-y-3">
              {d.optimized_headlines.map((headline, i) => (
                <div key={i} className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border border-indigo-100 dark:border-indigo-800/30 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs font-bold text-indigo-600 bg-indigo-100 rounded-full px-2 py-0.5">
                          Option {i + 1}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-slate-500">{headline.length}/220 chars</span>
                      </div>
                      <p className="text-sm font-medium text-gray-800 dark:text-white">{headline}</p>
                    </div>
                    <CopyButton text={headline} id={`headline-${i}`} copiedId={copiedId} onCopy={handleCopy} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </SectionCard>

      {/* ── About Section Optimizer ── */}
      <SectionCard title="About / Summary Optimization" score={d.about_score} icon={FileText}>
        <div className="pt-4 space-y-4">
          {/* Metrics */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl p-3 text-center">
              <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">Optimal Length</p>
              <p className="text-sm font-bold text-gray-800 dark:text-white">1,500–2,000</p>
              <p className="text-xs text-gray-400 dark:text-slate-500">characters</p>
            </div>
            <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl p-3 text-center">
              <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">Keyword Goal</p>
              <p className="text-sm font-bold text-gray-800 dark:text-white">10+ keywords</p>
              <p className="text-xs text-gray-400 dark:text-slate-500">from market demand</p>
            </div>
            <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl p-3 text-center">
              <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">Must Include</p>
              <p className="text-sm font-bold text-gray-800 dark:text-white">Call to Action</p>
              <p className="text-xs text-gray-400 dark:text-slate-500">at the end</p>
            </div>
          </div>

          {/* Generated About */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                AI-Generated About Section
              </p>
              <CopyButton text={d.optimized_about} id="about-section" copiedId={copiedId} onCopy={handleCopy} />
            </div>
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border border-indigo-100 dark:border-indigo-800/30 rounded-xl p-5">
              <div className={`text-sm text-gray-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap ${!aboutExpanded ? 'line-clamp-6' : ''}`}>
                {d.optimized_about}
              </div>
              <button
                onClick={() => setAboutExpanded(!aboutExpanded)}
                className="mt-3 text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
              >
                {aboutExpanded ? <><ChevronUp className="w-3 h-3" />Show less</> : <><ChevronDown className="w-3 h-3" />Read full section</>}
              </button>
            </div>
            <p className="text-xs text-gray-400 dark:text-slate-500 mt-2">
              Length: {d.optimized_about.length.toLocaleString()} characters
            </p>
          </div>
        </div>
      </SectionCard>

      {/* ── Skills Optimizer ── */}
      <SectionCard title="Skills Section Optimization" score={d.skills_score} icon={Star}>
        <div className="pt-4 grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Add these skills */}
          <div>
            <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
              Add to LinkedIn
            </p>
            <div className="space-y-2">
              {d.skills_to_add.length > 0 ? d.skills_to_add.map((skill, i) => (
                <div key={i} className="flex items-center justify-between bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/30 rounded-xl px-3 py-2">
                  <span className="text-sm text-gray-700 dark:text-slate-300 font-medium">{skill}</span>
                  <span className="text-xs text-amber-600 font-medium">+ Add</span>
                </div>
              )) : (
                <p className="text-xs text-gray-400 dark:text-slate-500 italic">No critical gaps found</p>
              )}
            </div>
          </div>

          {/* Reorder these */}
          <div>
            <p className="text-xs font-semibold text-primary-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-primary-500 inline-block" />
              Reorder to Top (by demand)
            </p>
            <div className="space-y-1.5">
              {d.skills_to_reorder.slice(0, 10).map((skill, i) => (
                <div key={i} className="flex items-center gap-2 bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800/30 rounded-xl px-3 py-1.5">
                  <span className="text-xs font-bold text-primary-400 w-5">{i + 1}.</span>
                  <span className="text-sm text-gray-700 dark:text-slate-300">{skill}</span>
                </div>
              ))}
              {d.skills_to_reorder.length === 0 && (
                <p className="text-xs text-gray-400 dark:text-slate-500 italic">Skills already ordered well</p>
              )}
            </div>
          </div>

          {/* Consider removing */}
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-gray-400 dark:bg-slate-500 inline-block" />
              Consider Removing
            </p>
            <div className="space-y-1.5">
              {d.skills_to_remove.length > 0 ? d.skills_to_remove.map((skill, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-800 rounded-xl px-3 py-1.5">
                  <span className="text-sm text-gray-400 dark:text-slate-500 line-through">{skill}</span>
                  <span className="text-xs text-gray-400 dark:text-slate-500 ml-auto">Low demand</span>
                </div>
              )) : (
                <p className="text-xs text-gray-400 dark:text-slate-500 italic">No low-value skills found</p>
              )}
              {d.skills_to_remove.length > 0 && (
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-2">
                  These skills have very low market demand. Removing them frees space for higher-impact skills.
                </p>
              )}
            </div>
          </div>
        </div>
      </SectionCard>

      {/* ── Recruiter Keywords ── */}
      <SectionCard title="Top Recruiter Search Keywords" score={undefined} icon={Eye}>
        <div className="pt-4">
          <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">
            These are the exact keywords recruiters in your target market use to find candidates.
            Make sure they appear naturally in your headline, About section, and experience bullets.
          </p>
          <div className="flex flex-wrap gap-2">
            {d.recruiter_keywords.map((kw, i) => {
              const size = i < 3 ? 'text-base px-4 py-2' : i < 6 ? 'text-sm px-3 py-1.5' : 'text-xs px-3 py-1';
              const color = i < 3
                ? 'bg-indigo-100 text-indigo-800 border-indigo-200'
                : i < 6
                  ? 'bg-purple-50 text-purple-700 border-purple-200'
                  : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400 border-gray-200 dark:border-slate-700';
              return (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.05 }}
                  className={`font-medium rounded-full border cursor-default ${size} ${color}`}
                >
                  {kw}
                </motion.span>
              );
            })}
          </div>
          <p className="text-xs text-gray-400 dark:text-slate-500 mt-4">
            Larger chips = higher search frequency. Prioritize including the top 3 in your headline.
          </p>
        </div>
      </SectionCard>

      {/* ── Experience Optimization ── */}
      <SectionCard title="Experience Bullet Optimization" score={d.experience_score} icon={TrendingUp} defaultExpanded={false}>
        <div className="pt-4">
          <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                <Zap className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-indigo-900 dark:text-indigo-300 mb-1">AI Optimization Tip</p>
                <p className="text-sm text-indigo-800 dark:text-indigo-400">{d.experience_tip}</p>
              </div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-600 dark:text-slate-400 mb-2">Formula for Power Bullets</p>
              <p className="text-sm text-gray-700 dark:text-slate-300 font-mono text-xs leading-relaxed">
                [Action Verb] + [Skill/Tool] + [Result] + [Scale]
              </p>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-2 italic">
                e.g., "Led cross-functional Python ML pipeline reducing churn 32% across 2M users"
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-600 dark:text-slate-400 mb-2">Top Keywords to Add to Bullets</p>
              <div className="flex flex-wrap gap-1.5">
                {d.recruiter_keywords.slice(0, 6).map((kw, i) => (
                  <span key={i} className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                    {kw}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* ── Profile Completeness Checklist ── */}
      <SectionCard title="Profile Completeness Checklist" score={d.completeness_score} icon={CheckCircle} defaultExpanded={false}>
        <div className="pt-4 space-y-2">
          {d.completeness_checklist.map((item, i) => (
            <div
              key={i}
              className={`flex items-start gap-3 p-3 rounded-xl border ${
                item.done
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-800/30'
                  : 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800/30'
              }`}
            >
              <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                item.done ? 'bg-green-500' : 'bg-red-400'
              }`}>
                {item.done
                  ? <CheckCircle className="w-3.5 h-3.5 text-white" />
                  : <span className="text-white text-xs font-bold">✗</span>}
              </div>
              <div className="flex-1">
                <p className={`text-sm font-medium ${item.done ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>
                  {item.label}
                </p>
                {item.note && (
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{item.note}</p>
                )}
              </div>
              {!item.done && (
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 font-medium"
                >
                  Fix <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          ))}
          <p className="text-xs text-gray-400 dark:text-slate-500 pt-2">
            Items marked "Cannot verify" require manual review on your LinkedIn profile.
          </p>
        </div>
      </SectionCard>

      {/* ── Footer ── */}
      <div className="bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-800 rounded-xl p-5 text-center">
        <p className="text-sm text-gray-600 dark:text-slate-400">
          Analysis powered by your{' '}
          <span className="font-semibold">{d.data_sources.resume_count} resume{d.data_sources.resume_count !== 1 ? 's' : ''}</span>,{' '}
          <span className="font-semibold">{d.data_sources.interview_count} interview session{d.data_sources.interview_count !== 1 ? 's' : ''}</span>,{' '}
          and live market data from active job postings.
        </p>
        <div className="mt-3 flex items-center justify-center gap-4">
          <Link to="/skill-gap-analysis" className="text-sm text-indigo-600 hover:text-indigo-800 font-medium underline">
            View deep skill gap analysis →
          </Link>
          <Link to="/intelligence" className="text-sm text-indigo-600 hover:text-indigo-800 font-medium underline">
            View intelligence dashboard →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LinkedInOptimizer;
